import { FC, useCallback, useMemo } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { ChartContainer } from 'src/components/chart/container'
import { Colors } from 'src/components/theme'
import { BuyVsSellOvertimeChartTooltip } from 'src/v2/views/owner-reveal/insights/portfolio-summary/buy-vs-sell-overtime/chart-tooltip'

const SERIES = [
  {
    name: 'Bought',
    animation: false,
    data: [
      { x: 2014, y: 12, amount: 22000 },
      { x: 2015, y: 14, amount: 25000 },
      { x: 2016, y: 10, amount: 20000 },
      { x: 2017, y: 16, amount: 30000 },
      { x: 2018, y: 18, amount: 35000 },
      { x: 2019, y: 21, amount: 40000 },
      { x: 2020, y: 17, amount: 100000 },
      { x: 2021, y: 14, amount: 50000 },
      { x: 2022, y: 10, amount: 30000 },
      { x: 2023, y: 7, amount: 20000 },
      { x: 2024, y: 10, amount: 30000 },
    ],
    color: Colors.primaryGreen600,
    marker: {
      symbol: 'circle',
    },
  },
  {
    name: 'Sold',
    animation: false,
    data: [
      { x: 2014, y: 7, amount: 125000 },
      { x: 2015, y: 9, amount: 150000 },
      { x: 2016, y: 5, amount: 100000 },
      { x: 2017, y: 11, amount: 200000 },
      { x: 2018, y: 13, amount: 250000 },
      { x: 2019, y: 16, amount: 300000 },
      { x: 2020, y: 12, amount: 150000 },
      { x: 2021, y: 9, amount: 100000 },
      { x: 2022, y: 5, amount: 50000 },
      { x: 2023, y: 2, amount: 25000 },
      { x: 2024, y: 5, amount: 50000 },
    ],
    color: Colors.primaryRed600,
    marker: {
      symbol: 'circle',
    },
  },
]

const BuyVsSellOvertimeChartInner: FC = () => {
  const tooltipNode = document.createElement('div')
  const tooltipRoot = createRoot(tooltipNode)

  const tooolTipFormatter = useCallback((ctx: Highcharts.TooltipFormatterContextObject) => {
    if (!ctx.points?.length) {
      return
    }

    const year = ctx.x ? Number(ctx.x) : 0
    const bought = ctx.points[0].y ?? 0
    const boughtAmount = (ctx.points[0].point as any).amount ?? 0
    const sold = ctx.points[1].y ?? 0
    const soldAmount = (ctx.points[1].point as any).amount ?? 0

    flushSync(() => {
      tooltipRoot.render(
        <BuyVsSellOvertimeChartTooltip
          bought={bought}
          sold={sold}
          year={year}
          boughtAmount={boughtAmount}
          soldAmount={soldAmount}
        />
      )
    })

    return tooltipNode.innerHTML
  }, [])

  const options = useMemo(() => {
    const options = {
      chart: {
        type: 'line',
        animation: false,
      },
      title: undefined,
      xAxis: {
        crosshair: {
          color: Colors.neutral500,
        },
        lineWidth: 0,
        tickWidth: 0,
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 500,
          },
        },
      },
      yAxis: {
        crosshair: false,
        tickAmount: 5,
        startOnTick: false,
        title: {
          text: '# of properties',
          style: {
            fontSize: '14px',
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: 500,
          },
        },
        min: 0,
      },
      legend: {
        enabled: true,
        itemStyle: { color: Colors.neutral400 },
        itemHoverStyle: { color: Colors.neutral400 },
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          stickyTracking: true,
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(0,0,0,0)',
        shadow: false,
        borderWidth: 0,
        style: {
          zIndex: 10,
        },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return tooolTipFormatter(this)
        },
      },
      series: SERIES,
    }

    return options as any
  }, [])

  return <ChartContainer options={options} />
}

export const BuyVsSellOvertimeChart: FC = () => {
  // TODO: Implement nodata once we have the data
  // if (nodata) {
  //   return (
  //     <WidgetCardEmpty message="It looks like we don't have data for this Owner yet. We're constantly gathering new data, so keep an eye out for updates." />
  //   )
  // }

  return <BuyVsSellOvertimeChartInner />
}
